import React from "react";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

import {
  getMissingMembershipsCount,
  isMigrationPreviewValid,
  MigrationPreviewResult,
} from "../../services/IntegrationApi";

interface PreviewStepProps {
  preview: MigrationPreviewResult | null;
}

export const PreviewStep: React.FC<PreviewStepProps> = ({ preview }) => {
  if (!preview) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isMigrationPreviewValid(preview)) {
    const missingCount = getMissingMembershipsCount(preview);
    return (
      <Grid container spacing={2} direction="column">
        <Grid item>
          <Alert severity="error" variant="outlined">
            <AlertTitle sx={{ fontWeight: 600 }}>CSV validation failed</AlertTitle>
            {missingCount} membership{missingCount === 1 ? " is" : "s are"} missing from the file.
            Please update the CSV and upload again.
          </Alert>
        </Grid>
      </Grid>
    );
  }

  const toBeMigrated = preview.toBeMigrated ?? 0;
  const toBeRemoved = preview.toBeRemoved ?? 0;

  return (
    <Grid container spacing={2} direction="column">
      <Grid item>
        <Alert severity="success" variant="outlined">CSV is valid. All memberships found.</Alert>
      </Grid>
      {toBeMigrated > 0 && (
        <Grid item>
          <Alert severity="info" variant="outlined">
            <strong>{toBeMigrated}</strong> membership
            {toBeMigrated === 1 ? "" : "s"} will be migrated to Entrata.
          </Alert>
        </Grid>
      )}
      {toBeRemoved > 0 && (
        <Grid item>
          <Alert severity="warning" variant="outlined">
            <AlertTitle sx={{ fontWeight: 600 }}>
              {toBeRemoved} membership
              {toBeRemoved === 1 ? "" : "s"} will be removed
            </AlertTitle>
            These memberships are missing Entrata IDs in the uploaded CSV.
          </Alert>
        </Grid>
      )}
      <Grid item>
        <Typography variant="body2" color="text.secondary">
          Memberships in Archived and Closed matching rounds will not be affected.
        </Typography>
      </Grid>
    </Grid>
  );
};
